// ── QUE EL COLOR DE LA CLÍNICA SE LEA SIEMPRE ───────────────────────────────────────────────────
//
// Cada clínica elige su color de marca en Ajustes y con él se tiñe la app. El problema es que un
// color elegido por su dueño no tiene por qué servir de fondo para texto: un amarillo, un beige o
// un lima dejan las letras blancas ilegibles, y un azul marino casi negro apaga la interfaz. La
// decisión del dueño es que esto se resuelva SOLO Y EN SILENCIO: la clínica pone su color y la app
// lo ajusta lo justo para que se lea. Nadie ve un botón ilegible y nadie tiene que entender de
// contraste para configurar su producto.
//
// DE DÓNDE SALE ESTO. `luminancia`, `textoSobre` y `marcaFuerte` son la MISMA lógica que ya lleva
// meses funcionando en los PDF de la clínica (backend/services/pdfTheme.js, líneas 27-37 y 70). Se
// copia en vez de importarse porque aquélla corre en Node (CommonJS, dentro de pdfkit) y ésta en el
// navegador (ESM, dentro de Vite): no hay forma de que una cargue a la otra sin arrastrar medio
// backend al bundle. Al ser dos copias, la regla es que TIENEN QUE DAR EL MISMO RESULTADO — si se
// tocara una y no la otra, la factura de la clínica y su pantalla saldrían de dos colores distintos
// y nadie lo vería hasta tener el papel al lado del monitor. Hay un guardia que las compara color a
// color: backend/routes/marcaClinica.test.js.

// El azul del cuaderno de marca. Está escrito aquí porque el JS no puede leer una variable de CSS
// antes de que el navegador pinte, y estos cálculos se hacen ANTES de inyectar nada. Tiene que
// coincidir con `--biango-azul` de index.css; hay un guardia que lo comprueba. (Se llama
// AZUL_MARCA y no con el nombre del producto en mayúsculas porque hay un guardia de la casa que
// exige que la marca se escriba siempre "Biango", tal cual.)
export const AZUL_MARCA = '#1E88E5'

// Un color de la base de datos NO entra en un `style` sin pasar por aquí. El servidor ya lo valida
// al guardarlo (routes/admin.js), pero el que llega al navegador viene por la red y acaba dentro de
// una propiedad CSS: si alguien colara `red; background: url(https://…)` en esa columna, estaría
// escribiendo CSS en la pantalla de todo el equipo. Seis dígitos hexadecimales y nada más.
export const hexOk = (c) => typeof c === 'string' && /^#[0-9a-fA-F]{6}$/.test(c)

// Aclara u oscurece un color acercándolo a otro un tanto por uno (0 = nada, 1 = el otro del todo).
export function mezcla(hex, conHex, t) {
  const a = hex.replace('#', ''), b = conHex.replace('#', '')
  const ai = [0, 2, 4].map(i => parseInt(a.slice(i, i + 2), 16))
  const bi = [0, 2, 4].map(i => parseInt(b.slice(i, i + 2), 16))
  const m = ai.map((v, i) => Math.round(v + (bi[i] - v) * t))
  return '#' + m.map(v => v.toString(16).padStart(2, '0')).join('')
}

// Luminancia relativa (fórmula WCAG). Es lo que decide si sobre ese fondo el texto tiene que ser
// claro u oscuro: el ojo no ve "claro" y "oscuro" por el valor del hexadecimal, sino por esto.
export function luminancia(hex) {
  const h = (hex || '').replace('#', '')
  const [r, g, b] = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16) / 255)
    .map(v => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)))
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

// Color de texto legible SOBRE un fondo dado: blanco si el fondo es oscuro, tinta si es claro.
export function textoSobre(bgHex, claro = '#ffffff', oscuroTxt = '#1f2937') {
  if (!hexOk(bgHex)) return claro
  return luminancia(bgHex) > 0.55 ? oscuroTxt : claro
}

// t271b (16-oct-2026) · EL TEXTO SOBRE EL BOTÓN PRINCIPAL EN MODO OSCURO, POR CONTRASTE. En oscuro el principal es la
// variante clara (`marcaClara`, luminancia 0,40-0,44 por contrato, para que no se hunda en el gris del fondo), y
// `textoSobre` da blanco hasta 0,55: blanco sobre ese gris claro se quedaba en 2,2:1 con CUALQUIER color, también el de
// Biango (#AAADB3), por debajo del 4,5:1 de la norma. Aquí se elige el de los dos (blanco o tinta) que más contraste dé
// —sobre la franja oscura, la tinta: de 6,2 a 6,8:1—. Es la que usan `--sobre-primary` del tema oscuro (index.css,
// html.dark) y `--marca-texto-oscuro`. `textoSobre` no se toca: es la copia de la de los PDF (services/pdfTheme.js) y
// las dos tienen que dar lo mismo.
export const TINTA_SOBRE_CLARO = '#1f2937'
export function textoConMasContraste(bgHex, claro = '#ffffff', tinta = TINTA_SOBRE_CLARO) {
  if (!hexOk(bgHex)) return claro
  return ratioContraste(bgHex, tinta) > ratioContraste(bgHex, claro) ? tinta : claro
}

// ── LOS TRES LISTONES ───────────────────────────────────────────────────────────────────────────
// EL BOTÓN PRIMARIO. El listón no es un número inventado: es la luminancia del propio azul de
// Biango. Toda la app está dibujada sobre ese azul con letras blancas encima, así que "se lee al
// menos tan bien como se lee hoy" es exactamente la condición correcta — ni más exigente (obligaría
// a ennegrecer marcas que ya son legibles) ni más laxa. Y como la comparación del bucle es `>`, una
// clínica que elija literalmente el azul de Biango no sufre ni un paso de oscurecido.
export const TOPE_PRIMARIO = luminancia(AZUL_MARCA)

// EL MENÚ LATERAL. Es una banda grande con texto blanco encima, parte de él al 70% de opacidad, así
// que pide bastante más margen que un botón: con este listón el texto activo va a 9,5:1 y el
// atenuado a 5,6:1. Más oscuro que esto ya no sería teñir el menú, sería apagarlo.
export const TOPE_MENU = 0.06

// EL MODO OSCURO, que no es un tope sino una FRANJA estrecha. El modo oscuro ya subía el primario
// al celeste de la marca (#6FB4F0), cuya luminancia es 0,42: el SUELO lo pone el fondo gris de la
// app (por debajo de 0,40 el color se hunde en él) y el TECHO lo pone el texto blanco que media app
// lleva escrito a mano encima de los botones primarios. Sin techo, una clínica de marca amarilla
// vería en oscuro botones blancos sobre amarillo — y esos "#fff" están repartidos por cientos de
// pantallas, no en una hoja de estilos. Con la franja, cualquier marca cae en el MISMO escalón que
// la app ya usaba (6,5:1 contra el fondo, 2,2:1 con el texto blanco) y nada de lo de encima cambia.
export const PISO_OSCURO = 0.40
export const TECHO_OSCURO = 0.44

// Oscurece SOLO lo necesario para que encima quepa texto claro. Se va en pasos pequeños en vez de
// aplicar un porcentaje fijo porque un 45% de golpe sobre una marca ya oscura la deja negra y la
// clínica pierde su color: quien elige un granate quiere ver granate, no un rectángulo negro.
// `tope` y `pasos` valen por defecto lo mismo que en pdfTheme.js para que las dos copias coincidan.
export function marcaFuerte(hex, tope = 0.34, pasos = 6) {
  let c = hex
  for (let i = 0; i < pasos && luminancia(c) > tope; i++) c = mezcla(c, '#000000', 0.14)
  return c
}

// El espejo de la anterior, para el modo oscuro: lleva el color a la franja de arriba, subiéndolo si
// se hunde en el fondo gris y bajándolo si se pasa de claro. Un color de marca a plena saturación
// sobre el gris oscuro de la app se lee mal (el propio azul de Biango se queda en 3,1:1) y un marino
// corporativo directamente desaparece. Esto es lo que ya hacía el modo oscuro a mano subiendo al
// celeste de la paleta; ahora lo hace con el color de cada clínica.
// El paso es del 6% y no del 14% como en `marcaFuerte`: la franja de arriba es estrecha y un salto
// del 14% se la pasa de largo, dejando el color por debajo del suelo del que veníamos.
export function marcaClara(hex, piso = PISO_OSCURO, techo = TECHO_OSCURO, pasos = 30) {
  let c = hex
  for (let i = 0; i < pasos && luminancia(c) < piso; i++) c = mezcla(c, '#ffffff', 0.06)
  for (let i = 0; i < pasos && luminancia(c) > techo; i++) c = mezcla(c, '#000000', 0.06)
  return c
}

// ── LOS DOS COLORES DE BIANGO PARA LA INTERFAZ (5-oct-2026) ─────────────────────────────────────
// Los eligió el dueño para lo que no es de ninguna clínica (Superadmin, entrar, la invitación, recuperar la contraseña,
// el asistente por enlace y la firma con Biango) y para la clínica que no ha elegido color: «usa mejor este color, que
// es menos llamativo: #1B2130, y por si necesitas uno para contrastar las pestañas en uso o algo así, este, que
// contrasta algo: #465066». Con letra blanca encima, 16,1:1 y 8,1:1. Tienen que ser los mismos que `--biango-principal`
// y `--biango-secundario` de index.css (lo comprueba backend/routes/color-secundario.test.js). El logotipo sigue con
// los suyos (AZUL_MARCA, arriba).
export const PRINCIPAL_BIANGO = '#1B2130'
export const SECUNDARIO_BIANGO = '#465066'

// Cuánto contrastan dos colores (la fórmula de la norma WCAG): 1 es nada y 21 es negro sobre blanco.
export function ratioContraste(a, b) {
  const x = luminancia(a), y = luminancia(b)
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

// ── EL COLOR SECUNDARIO (5-oct-2026) ────────────────────────────────────────────────────────────
// El dueño: «añadimos un color secundario para el enlace activo del sidebar y para otras zonas donde se pueda usar y
// contrastar». Va de RELLENO: la entrada activa del menú lateral, la pestaña activa y las insignias (index.css ·
// `--secundario`). La clínica lo elige en «Datos y marca» o lo deja vacío, y entonces se calcula de su color principal.
//
// LA NORMA PARA EL TEXTO (4,5:1). El texto que va encima lo pone la app: blanco o tinta oscura, el que más contraste
// dé (`contrasteSecundario`). Si con un color ELEGIDO ni uno ni otro llega —un gris o un azul de luminancia media—,
// «Datos y marca» lo avisa al elegirlo y la app usa el mejor de los dos: el color es de la clínica y no se le toca.
export const CONTRASTE_TEXTO = 4.5
export const TINTA_SECUNDARIO = '#1f2937'

// EL CALCULADO, CUANDO LA CLÍNICA NO ELIGE NINGUNO. El modelo es el par del dueño: #465066 sobre #1B2130. Del color
// principal sale un tono que (1) admite LETRA BLANCA con la norma —así puede rellenar cualquier sitio donde hoy va el
// principal con el blanco escrito a mano, sin dejar nada ilegible— y (2) se distingue del menú lateral al menos tanto
// como el secundario de Biango del suyo (`CONTRASTE_MENU`, 2:1). Se oscurece lo justo si no admite el blanco y se aclara
// lo justo si se confunde con el menú, en pasos pequeños para no pasarse: un rojo o un verde de luminancia media se
// quedan tal cual (la pestaña activa sigue siendo SU color, como hasta hoy), un marino se aclara y un amarillo se
// oscurece. (Un pastel, que fue la primera idea, dejaba la pestaña activa de una marca clara invisible sobre blanco.)
export const CONTRASTE_MENU = ratioContraste(SECUNDARIO_BIANGO, PRINCIPAL_BIANGO)
export function secundarioDe(color) {
  if (!hexOk(color)) return null
  const menu = marcaFuerte(color, TOPE_MENU, 12)
  let c = color
  for (let i = 0; i < 80 && ratioContraste(c, '#ffffff') < CONTRASTE_TEXTO; i++) c = mezcla(c, '#000000', 0.02)
  for (let i = 0; i < 80 && ratioContraste(c, menu) < CONTRASTE_MENU; i++) {
    const mas = mezcla(c, '#ffffff', 0.01)
    if (ratioContraste(mas, '#ffffff') < CONTRASTE_TEXTO) break
    c = mas
  }
  return c
}

// { texto, ratio, ok }: el texto que va encima de ese relleno, cuánto contrasta y si llega a la norma.
export function contrasteSecundario(fondo) {
  if (!hexOk(fondo)) return null
  const blanco = ratioContraste(fondo, '#ffffff'), tinta = ratioContraste(fondo, TINTA_SECUNDARIO)
  const texto = blanco >= tinta ? '#ffffff' : TINTA_SECUNDARIO
  const ratio = Math.max(blanco, tinta)
  return { texto, ratio, ok: ratio >= CONTRASTE_TEXTO }
}

// El secundario que de verdad se usa: el elegido; si no, el calculado del principal; y sin ninguno de los dos, el de
// Biango. Es lo que enseña «Datos y marca» en su selector y en su vista previa.
export function secundarioEfectivo(principal, elegido) {
  if (hexOk(elegido)) return elegido
  return secundarioDe(principal) || SECUNDARIO_BIANGO
}

// Las dos variables del secundario: el relleno y su texto. null sin ningún color válido (la hoja de estilos se queda
// con el de Biango).
export function tokensSecundario(color, secundario) {
  const s = hexOk(secundario) ? secundario : secundarioDe(color)
  if (!s) return null
  return { '--marca-secundario': s, '--marca-sobre-secundario': contrasteSecundario(s).texto }
}

// Los tres colores que la app necesita de una marca, más el texto que va encima de cada uno, y el secundario con el
// suyo. Se calculan juntos y en un solo sitio para que no haya dos pantallas haciendo la cuenta a su manera.
// Devuelve null si no hay ningún color válido: sin color de clínica la app se queda como está, con los colores de
// Biango, y eso NO es un caso de error sino el caso normal de la mayoría de instalaciones. Con solo el secundario
// elegido (sin principal), devuelve solo las dos del secundario.
export function tokensDeMarca(color, secundario) {
  const sec = tokensSecundario(color, secundario)
  if (!hexOk(color)) return sec
  const claro = marcaFuerte(color, TOPE_PRIMARIO)
  const oscuro = marcaClara(color)
  // El menú admite más pasos que el resto: partiendo de un amarillo o un blanco hacen falta nueve
  // para llegar al listón, y con los seis de pdfTheme se quedaría a medias y con texto ilegible.
  const menu = marcaFuerte(color, TOPE_MENU, 12)
  return {
    '--marca-clinica': claro,
    '--marca-clinica-oscuro': oscuro,
    '--marca-clinica-menu': menu,
    '--marca-texto': textoSobre(claro),
    // t271b · por contraste (ver `textoConMasContraste`): sobre la variante clara del modo oscuro, la tinta.
    '--marca-texto-oscuro': textoConMasContraste(oscuro),
    '--marca-texto-menu': textoSobre(menu),
    ...sec,
  }
}
